import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Sparkles, ShoppingBag, Zap, DollarSign, Crown, Star, Music, Gift, Rocket, Heart, Bell, Mic, BookOpen,
} from 'lucide-react';
import { CHANGELOG, CHANGELOG_VERSION } from '@/data/changelog';

const SEEN_KEY = '1samdigital_whatsnew_seen';

const ICONS = { Sparkles, ShoppingBag, Zap, DollarSign, Crown, Star, Music, Gift, Rocket, Heart, Bell, Mic, BookOpen };

const HighlightRow = ({ icon, text, index }) => {
  const Icon = ICONS[icon] || Sparkles;
  return (
    <motion.div
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.1 + index * 0.08 }}
      className="flex items-start gap-3"
      data-testid="whats-new-highlight"
    >
      <div className="shrink-0 w-9 h-9 rounded-xl bg-[#7000FF]/15 border border-[#7000FF]/30 flex items-center justify-center">
        <Icon className="w-4.5 h-4.5 text-[#9b6bff]" />
      </div>
      <p className="text-sm text-white/80 leading-relaxed pt-1">{text}</p>
    </motion.div>
  );
};

const WhatsNewModal = () => {
  const [open, setOpen] = useState(false);
  const latest = CHANGELOG[0];
  const earlier = CHANGELOG.slice(1);

  // Auto-open once per new release
  useEffect(() => {
    const seen = localStorage.getItem(SEEN_KEY);
    if (seen !== CHANGELOG_VERSION) {
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  // Allow manual open from anywhere (e.g. navbar button)
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('open-whats-new', handler);
    return () => window.removeEventListener('open-whats-new', handler);
  }, []);

  const markSeen = useCallback(() => {
    localStorage.setItem(SEEN_KEY, CHANGELOG_VERSION);
    window.dispatchEvent(new Event('whatsnew-seen'));
  }, []);

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) markSeen();
  };

  if (!latest) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 max-w-md p-0 overflow-hidden gap-0"
        data-testid="whats-new-modal"
      >
        {/* Header band */}
        <div className="relative px-6 pt-6 pb-5 bg-gradient-to-br from-[#7000FF]/25 via-[#7000FF]/5 to-transparent">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-[#7000FF] flex items-center justify-center shadow-lg shadow-[#7000FF]/30">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="text-xs font-semibold tracking-widest text-[#9b6bff] uppercase">What's New</span>
            <span className="ml-auto text-xs text-white/40">{latest.date}</span>
          </div>
          <DialogTitle className="font-heading text-2xl font-bold text-white">{latest.title}</DialogTitle>
          <DialogDescription className="sr-only">Latest updates and new features on 1SamDigital Music.</DialogDescription>
        </div>

        {/* Latest highlights */}
        <div className="px-6 py-5 space-y-4">
          {latest.highlights.map((h, i) => (
            <HighlightRow key={i} icon={h.icon} text={h.text} index={i} />
          ))}
        </div>

        {/* Earlier updates */}
        {earlier.length > 0 && (
          <div className="px-6 pb-2">
            <p className="text-[11px] uppercase tracking-wider text-white/30 mb-2">Earlier updates</p>
            <div className="space-y-1.5">
              {earlier.map((rel) => (
                <div key={rel.version} className="flex items-center gap-2 text-xs text-white/50">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/20" />
                  <span className="text-white/70">{rel.title}</span>
                  <span className="ml-auto text-white/30">{rel.date}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-5">
          <Button
            className="btn-primary w-full"
            onClick={() => handleOpenChange(false)}
            data-testid="whats-new-dismiss-btn"
          >
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsNewModal;
